import React, { useState, useEffect } from "react";
import { Video, ExternalLink, Info } from "lucide-react";
import TimePicker from "./TimePicker";
import DatePicker from "./DatePicker";
import { googleCalendarUrl, DEFAULT_DURATION, defaultWhen } from "../utils/calendar";
import { getValidUrl } from "../utils/Utils";
import ImageField from "./ImageField";
const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:3005";

const MeetingCard = ({ isOpen, onClose }) => {
    const [formData, setFormData] = useState(() => ({
        name: "",
        ...defaultWhen(),
        category: "",
        description: "",
        urlMeeting: "",
        meetingImage: "",
    }));

    const categorias = [
        "Charla ténica",
        "Dudas",
    ];

    useEffect(() => {
        if (isOpen) {
            // Resetear formulario cuando se abre
            // Fecha y hora salen rellenas: así el botón de Google está activo
            // desde el principio y no hay que tocarlas para poder usarlo.
            setFormData({
                name: "",
                ...defaultWhen(),
                category: "",
                description: "",
                urlMeeting: "",
                meetingImage: "",
            });
        }
    }, [isOpen]);

    const handleChange = (field) => (e) => {
        setFormData({ ...formData, [field]: e.target.value });
    };

    // Abre Google Calendar con lo ya escrito y la videollamada ya creada.
    // La hora es obligatoria: sin ella Google recibe un rango de día completo,
    // marca "Todo el día" y deja los campos de hora bloqueados.
    const canOpenGoogle = Boolean(formData.name && formData.date && formData.time);

    const openGoogleCalendar = () => {
        const url = googleCalendarUrl({
            title: formData.name,
            date: formData.date,
            time: formData.time,
            minutes: DEFAULT_DURATION.meeting,
            details: formData.description,
            meet: true,
        });
        if (url) window.open(url, "_blank", "noopener,noreferrer");
    };

    const saveMeetingData = async (MeetingData) => {
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                alert("No estás autenticado. Por favor, inicia sesión.");
                window.location.href = "/";
                return null;
            }

            const response = await fetch(`${baseUrl}/meetings/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(MeetingData),
            });

            const data = await response.json();
            if (!response.ok) {
                alert(data.message || "Error al guardar el Meeting");
                return null;
            }
            return data;
        } catch (error) {
            console.error("Error al guardar el Meeting:", error);
            alert("Error al guardar el Meeting");
            return null;
        }
    };

    const saveData = async () => {
        const userId = localStorage.getItem("userId");
        if (!userId) {
            alert("No se encontró el usuario.");
            return;
        }

        const MeetingData = {
            name: formData.name,
            date: formData.date,
            time: formData.time,
            category: formData.category,
            description: formData.description,
            urlMeeting: formData.urlMeeting,
            // En base64 lo sube el backend; una URL escrita a mano se normaliza
            meetingImage: formData.meetingImage.startsWith("data:")
                ? formData.meetingImage
                : getValidUrl(formData.meetingImage.trim()) || '',
            creator: userId,
        };

        const savedMeeting = await saveMeetingData(MeetingData);
        if (!savedMeeting) return;

        alert("Meeting creado con éxito. Será revisado por los administradores.");
        window.location.reload();
    };

    const handleSubmit = () => {
        if (
            !formData.name ||
            !formData.date ||
            !formData.time ||
            !formData.category ||
            !formData.description
        ) {
            alert("Por favor, completa todos los campos obligatorios.");
            return;
        }
        saveData();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div
            className="dyd-overlay"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="dyd-modal"
            >
                <div className="dyd-modal-head">
          <span className="dyd-modal-icon"><Video size={20} /></span>
          <h2>Crear nuevo Meeting</h2>
        </div>

                <div className="dyd-field">
                    <label className="dyd-label">
                        Nombre del Meeting *
                    </label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={handleChange("name")}
                        placeholder="Nombre del Meeting"
                        className="dyd-input"
                    />
                </div>

                <div style={{ display: "flex", gap: "12px" }}>
                    <div className="dyd-field" style={{ flex: 1 }}>
                        <label className="dyd-label">
                            Fecha *
                        </label>
                        <DatePicker
                            value={formData.date}
                            onChange={(date) => setFormData((f) => ({ ...f, date }))}
                        />
                    </div>

                    <div className="dyd-field" style={{ flex: 1 }}>
                        <label className="dyd-label">
                            Hora *
                        </label>
                        <TimePicker
                            value={formData.time}
                            onChange={(time) => setFormData((f) => ({ ...f, time }))}
                        />
                    </div>
                </div>

                <div className="dyd-field">
                    <label className="dyd-label">
                        Categoría *
                    </label>
                    <select
                        value={formData.category}
                        onChange={handleChange("category")}
                        className="dyd-select"
                    >
                        <option value="">Selecciona una categoría</option>
                        {categorias.map((cat) => (
                            <option key={cat} value={cat}>
                                {cat}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="dyd-field">
                    <label className="dyd-label">
                        Descripción *
                    </label>
                    <textarea
                        value={formData.description}
                        onChange={handleChange("description")}
                        placeholder="Describe el Meeting, actividades, requisitos, etc."
                        rows={4}
                        className="dyd-textarea"
                    />
                </div>

                <div className="dyd-field">
                    <label className="dyd-label">
                        Url meeting *
                    </label>

                    {/* Atajo: crea el evento en Google con estos datos y trae el enlace */}
                    <button
                        type="button"
                        onClick={openGoogleCalendar}
                        disabled={!canOpenGoogle}
                        className="dyd-btn dyd-btn-ghost dyd-btn-sm dyd-btn-block"
                        style={{ marginBottom: 4 }}
                        title={canOpenGoogle
                            ? "Abre Google Calendar con los datos y el Meet ya creados"
                            : "Rellena antes el nombre, la fecha y la hora"}
                    >
                        <Video size={15} />
                        Crear en Google Calendar con Meet
                        <ExternalLink size={13} />
                    </button>

                    <small className="dyd-hint" style={{ marginBottom: 4 }}>
                        {canOpenGoogle
                            ? <>Se abre en otra pestaña con los datos rellenos y el enlace de
                               <strong> Google Meet</strong> ya creado. Guarda, cópialo y pégalo aquí.</>
                            : <>Rellena <strong>nombre, fecha y hora</strong> para poder crearlo en Google.</>}
                    </small>

                    <textarea
                        value={formData.urlMeeting}
                        onChange={handleChange("urlMeeting")}
                        placeholder="https://meet.google.com/..."
                        rows={2}
                        className="dyd-textarea"
                    />
                </div>


                <ImageField
                    label="Imagen del meeting (opcional)"
                    value={formData.meetingImage}
                    onChange={(meetingImage) => setFormData((f) => ({ ...f, meetingImage }))}
                    hint="Decorativa: si no pones ninguna, la tarjeta sale con el icono de vídeo."
                />

                <div className="dyd-note">
                    <Info size={16} />
                    <small>
                        <strong>Nota:</strong> Tu Meeting se publicará al crearse.
                    </small>
                </div>

                <div className="dyd-modal-actions">
                    <button
                        onClick={handleSubmit}
                        className="dyd-btn dyd-btn-success"
                    >
                        Crear Meeting
                    </button>

                    <button
                        onClick={onClose}
                        className="dyd-btn dyd-btn-subtle"
                    >
                        Cancelar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MeetingCard;
